import React, { useState, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Card, Row, Col, Typography, Grid } from 'antd';
import { useAuth } from '@clerk/clerk-react';
import { ONBOARDING_STEPS, STEP_CONFIG } from './constants';
import { THEME_COLORS, GRADIENTS, SHADOWS, COLORS } from '../../utils/colors';
import StepIndicator from './components/StepIndicator';
import SignUpStep from './components/SignUpStep';
import DealershipDetailsStep from './components/DealershipDetailsStep';
import PlanTermsStep from './components/PlanTermsStep';
import BillingStep from './components/BillingStep';
import FinishStep from './components/FinishStep';
import './Onboarding.css';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const Onboarding = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();
  const history = useHistory();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Read ?step= from URL to jump to a specific step (e.g. ?step=dealership_details, ?step=finish_setup for testing)
  const stepFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const step = params.get('step');
    if (!step) return null;
    if (step === 'finish_setup' || step === 'finish') return ONBOARDING_STEPS.FINISH;
    if (Object.values(ONBOARDING_STEPS).includes(step)) return step;
    return null;
  };

  // Load saved step and form data from localStorage on mount; URL ?step= overrides for direct navigation
  const [currentStep, setCurrentStep] = useState(() => {
    const urlStep = stepFromUrl();
    if (urlStep) return urlStep;
    const savedStep = localStorage.getItem('onboarding_current_step');
    return savedStep || ONBOARDING_STEPS.SIGNUP;
  });
  const [formData, setFormData] = useState(() => {
    const savedFormData = localStorage.getItem('onboarding_form_data');
    return savedFormData ? JSON.parse(savedFormData) : {};
  });

  // Reset onboarding state when component mounts if user is not signed in
  // Skip reset if user explicitly navigated to a step via URL (e.g. ?step=dealership_details)
  useEffect(() => {
    if (isLoaded && !isSignedIn && !stepFromUrl()) {
      localStorage.removeItem('onboarding_current_step');
      setCurrentStep(ONBOARDING_STEPS.SIGNUP);
    }
  }, [isLoaded, isSignedIn, location.search]);
  
  // When URL has ?step=, keep currentStep in sync (e.g. direct link to dealership)
  useEffect(() => {
    const urlStep = stepFromUrl();
    if (urlStep && urlStep !== currentStep) setCurrentStep(urlStep);
  }, [location.search]);

  // Save step to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('onboarding_current_step', currentStep);
  }, [currentStep]);

  // Only use step= URLs: keep URL in sync with current step; normalize /onboarding with no step to ?step=signup
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlStep = params.get('step');
    if (urlStep === currentStep) return;
    history.replace({ pathname: '/onboarding', search: `?step=${currentStep}` });
  }, [currentStep, location.search, history]);
  
  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('onboarding_form_data', JSON.stringify(formData));
  }, [formData]);

  // Allow access to any step without authentication when navigated via URL (e.g. ?step=plan_terms)
  // For testing: allow Billing step without sign-in so the payment page can be viewed
  // Otherwise if not signed in and on a step that normally requires prior steps, reset to dealership details
  useEffect(() => {
    const urlStep = stepFromUrl();
    if (urlStep && currentStep === urlStep) return; // User explicitly requested this step via URL, allow it
    if (isLoaded && !isSignedIn && currentStep !== ONBOARDING_STEPS.SIGNUP && currentStep !== ONBOARDING_STEPS.DEALERSHIP_DETAILS && currentStep !== ONBOARDING_STEPS.BILLING) {
      setCurrentStep(ONBOARDING_STEPS.DEALERSHIP_DETAILS);
    }
  }, [isLoaded, isSignedIn, currentStep, location.search]);

  // Skip sign-up step if already authenticated - always go to dealership details after sign up
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      // If user is signed in and on signup step, go to dealership details
      if (currentStep === ONBOARDING_STEPS.SIGNUP) {
        setCurrentStep(ONBOARDING_STEPS.DEALERSHIP_DETAILS);
      }
      // If user is signed in and somehow on finish step without completing previous steps, reset to dealership details
      // Only allow finish step if we have formData indicating previous steps were completed (dealership data)
      const hasCompletedSteps = formData.dealership_name || formData.website || formData.name;
      if (currentStep === ONBOARDING_STEPS.FINISH && !hasCompletedSteps) {
        // Reset to dealership details if they haven't actually completed the steps
        setCurrentStep(ONBOARDING_STEPS.DEALERSHIP_DETAILS);
        // Clear the invalid finish step from localStorage
        localStorage.removeItem('onboarding_current_step');
      }
    }
  }, [isLoaded, isSignedIn, currentStep, formData]);

  // Scroll to top when step changes
  useEffect(() => {
    // Scroll window to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Also scroll any scrollable containers to top
    const scrollableContainers = document.querySelectorAll('[style*="overflow-y: auto"], [style*="overflowY: auto"]');
    scrollableContainers.forEach(container => {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [currentStep]);

  const handleNext = (data = {}) => {
    setFormData(prev => ({ ...prev, ...data }));
    
    const currentIndex = STEP_CONFIG.findIndex(step => step.key === currentStep);
    if (currentIndex < STEP_CONFIG.length - 1) {
      setCurrentStep(STEP_CONFIG[currentIndex + 1].key);
    }
  };

  const handleBack = () => {
    const currentIndex = STEP_CONFIG.findIndex(step => step.key === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEP_CONFIG[currentIndex - 1].key);
      // Scroll to top when going back
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const scrollableContainers = document.querySelectorAll('[style*="overflow-y: auto"], [style*="overflowY: auto"]');
      scrollableContainers.forEach(container => {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case ONBOARDING_STEPS.SIGNUP:
        return <SignUpStep onNext={handleNext} onBack={handleBack} />;
      case ONBOARDING_STEPS.DEALERSHIP_DETAILS:
        return <DealershipDetailsStep onNext={handleNext} onBack={handleBack} formData={formData} />;
      case ONBOARDING_STEPS.PLAN_TERMS:
        return <PlanTermsStep onNext={handleNext} onBack={handleBack} dealershipData={formData} />;
      case ONBOARDING_STEPS.BILLING:
        return <BillingStep onNext={handleNext} onBack={handleBack} formData={formData} />;
      case ONBOARDING_STEPS.FINISH:
        return <FinishStep formData={formData} />;
      default:
        return <DealershipDetailsStep onNext={handleNext} onBack={handleBack} />;
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: GRADIENTS.DARK.PRIMARY,
      padding: '0'
    }}>
      {isMobile ? (
        currentStep === ONBOARDING_STEPS.SIGNUP ? (
          /* Mobile: Step 1 Create Account - premium full-page layout (no generic header) */
          <SignUpStep onNext={handleNext} onBack={handleBack} isMobile />
        ) : (
        // Mobile Layout (steps 2–5)
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: '100vh',
          maxWidth: '100vw',
          background: GRADIENTS.DARK.PRIMARY
        }}>
          {/* Mobile Header */}
          <div style={{
            padding: '24px 20px',
            paddingBottom: '20px',
            textAlign: 'center'
          }}>
            {/* Title */}
            <Title level={2} style={{ 
              margin: 0,
              marginBottom: '12px',
              fontSize: '28px',
              fontWeight: 700,
              lineHeight: '1.2',
              background: 'linear-gradient(90deg, #60A5FA 0%, #A855F7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textAlign: 'center'
            }}>
              Welcome to Hooptie
            </Title>
            
            {/* Subtitle */}
            <Text style={{ 
              color: '#9CA3AF',
              fontSize: '14px',
              lineHeight: '1.5',
              marginBottom: '12px',
              display: 'block',
              textAlign: 'center'
            }}>
              AI-powered market insights and accurate pricing based on real active + sold listings in your area.
            </Text>
            
            {/* Step Indicator */}
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <Text style={{ 
                color: '#9CA3AF',
                fontSize: '13px',
                fontWeight: 500
              }}>
                Step {STEP_CONFIG.findIndex(step => step.key === currentStep) + 1} of {STEP_CONFIG.length}
              </Text>
              <div style={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                background: '#60A5FA',
                boxShadow: '0 0 6px rgba(96, 165, 250, 0.6)'
              }} />
              <Text style={{ 
                color: '#9CA3AF',
                fontSize: '13px',
                fontWeight: 500
              }}>
                {STEP_CONFIG.find(step => step.key === currentStep)?.title || 'Setup'}
              </Text>
            </div>
          </div>
          
          {/* Mobile Content */}
          <div style={{ 
            flex: 1,
            padding: '0 20px 20px 20px',
            overflowY: 'auto',
            paddingBottom: '100px'
          }}>
            {renderCurrentStep()}
          </div>
        </div>
        )
      ) : (
        // Desktop Layout - premium dark glass (step 1)
        <div
          className="onboarding-page-wrap"
          style={{
            display: 'flex',
            height: '100vh',
            maxWidth: '100vw',
          }}
        >
          {/* Left Sidebar - Steps */}
          <div style={{
            width: '280px',
            background: 'rgba(15, 23, 42, 0.6)',
            borderRight: '1px solid rgba(255,255,255,0.06)',
            padding: '32px 24px',
            overflowY: 'auto',
            position: 'relative',
            zIndex: 1,
          }}>
            <StepIndicator currentStep={currentStep} />
          </div>

          {/* Main Content Area */}
          <div className="onboarding-main-content">
            {/* Header */}
            <div className="onboarding-header-wrap">
              <Title level={2} className="onboarding-welcome-title">
                Welcome to Hooptie
              </Title>
              <Text className="onboarding-step-badge">
                Step {STEP_CONFIG.findIndex(step => step.key === currentStep) + 1} of {STEP_CONFIG.length}
              </Text>
            </div>

            {/* Content Card + footer outside card when signup */}
            <div className={`onboarding-content-inner${currentStep === ONBOARDING_STEPS.BILLING ? ' onboarding-content-inner-billing' : ''}${currentStep === ONBOARDING_STEPS.FINISH ? ' onboarding-content-inner-finish' : ''}`}>
              <div className="onboarding-card-wrap">
                <Card
                  className={currentStep === ONBOARDING_STEPS.SIGNUP ? 'onboarding-card-glass' : (currentStep === ONBOARDING_STEPS.PLAN_TERMS || currentStep === ONBOARDING_STEPS.BILLING) ? 'onboarding-card-no-scroll' : ''}
                  style={{
                    height: '100%',
                    maxWidth: '100%',
                    borderRadius: currentStep === ONBOARDING_STEPS.SIGNUP ? undefined : 20,
                    boxShadow: currentStep === ONBOARDING_STEPS.SIGNUP ? undefined : SHADOWS.DARK.XL,
                    background: currentStep === ONBOARDING_STEPS.SIGNUP ? undefined : THEME_COLORS.DARK.bg.secondary,
                    border: currentStep === ONBOARDING_STEPS.SIGNUP ? undefined : `1px solid ${THEME_COLORS.DARK.border.secondary}`,
                    color: THEME_COLORS.DARK.text.primary,
                    overflow: currentStep === ONBOARDING_STEPS.SIGNUP ? undefined : (currentStep === ONBOARDING_STEPS.PLAN_TERMS || currentStep === ONBOARDING_STEPS.BILLING) ? 'hidden' : 'auto',
                  }}
                >
                  <div style={{ padding: '0', height: '100%', overflow: (currentStep === ONBOARDING_STEPS.PLAN_TERMS || currentStep === ONBOARDING_STEPS.BILLING) ? 'hidden' : undefined }}>
                    {renderCurrentStep()}
                  </div>
                </Card>
              </div>
              {currentStep === ONBOARDING_STEPS.SIGNUP && (
                <div className="onboarding-card-footer">
                  By continuing, you agree to Hooptie's{' '}
                  <Link to="/terms-of-service" style={{ color: COLORS.PRIMARY[400] }}>Terms</Link>
                  {' & '}
                  <Link to="/privacy-policy" style={{ color: COLORS.PRIMARY[400] }}>Privacy</Link>.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding;